import React, { useState, useEffect } from 'react';
import { Award, X, Printer } from 'lucide-react';
import { getSettings, getCourseById, UPLOAD_URL, getMediaUrl } from '../services/api';

const CertificateModal = ({
    isOpen,
    onClose,
    studentName,
    courseTitle,
    instructor,
    completionDate,
    courseId,
    examScore,
    enrolledAt,
    studentPhoto
}) => {
    const [adminSettings, setAdminSettings] = useState({});
    const [courseDetails, setCourseDetails] = useState(null);
    const [loadingCourse, setLoadingCourse] = useState(false);

    useEffect(() => {
        if (!isOpen) return;

        const fetchSettingsAndCourse = async () => {
            try {
                const settingsRes = await getSettings();
                if (settingsRes.success) {
                    setAdminSettings(settingsRes.data);
                }
            } catch (error) {
                console.error("Error fetching settings in CertificateModal:", error);
            }

            const id = courseId;
            if (id) {
                try {
                    setLoadingCourse(true);
                    const courseRes = await getCourseById(id);
                    if (courseRes.success) {
                        setCourseDetails(courseRes.data);
                    }
                } catch (error) {
                    console.error("Error fetching course details in CertificateModal:", error);
                } finally {
                    setLoadingCourse(false);
                }
            }
        };

        fetchSettingsAndCourse();
    }, [isOpen, courseId]);

    if (!isOpen) return null;

    // Dates calculations helper
    const getCertificateDates = () => {
        let start = new Date();
        if (enrolledAt) {
            const parsed = new Date(enrolledAt);
            if (!isNaN(parsed.getTime())) {
                start = parsed;
            }
        } else if (completionDate) {
            const parsed = new Date(completionDate);
            if (!isNaN(parsed.getTime())) {
                start = new Date(parsed.getTime() - 11 * 24 * 60 * 60 * 1000);
            }
        }

        // End date is enrollment + 11 days (standard course duration mapping) or the completion date
        let end = new Date(start.getTime() + 11 * 24 * 60 * 60 * 1000);
        if (completionDate) {
            const parsed = new Date(completionDate);
            if (!isNaN(parsed.getTime())) {
                end = parsed;
            }
        }

        // Issue date is end date + 2 days
        let issue = new Date(end.getTime() + 2 * 24 * 60 * 60 * 1000);

        const pad = (num) => String(num).padStart(2, '0');

        // Format as DD-MM-YY
        const formatDuring = (d) => {
            const dd = pad(d.getDate());
            const mm = pad(d.getMonth() + 1);
            const yy = String(d.getFullYear()).slice(-2);
            return `${dd}-${mm}-${yy}`;
        };

        // Format as DD-MMM-YYYY (e.g., 01-May-2026)
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const formatIssue = (d) => {
            const dd = pad(d.getDate());
            const mmm = months[d.getMonth()];
            const yyyy = d.getFullYear();
            return `${dd}-${mmm}-${yyyy}`;
        };

        return {
            during: `${formatDuring(start)} to ${formatDuring(end)}`,
            issueDate: formatIssue(issue)
        };
    };

    const dates = getCertificateDates();

    // Registration number format: LH/{Year-NextYear}/{padded student ID or dynamic number}
    const getRegistrationNumber = () => {
        let enrollYear = 2026;
        if (enrolledAt) {
            const parsed = new Date(enrolledAt);
            if (!isNaN(parsed.getTime())) {
                enrollYear = parsed.getFullYear();
            }
        }
        const nextYear = enrollYear + 1;
        // Generate a deterministic but unique registration sequence
        const seedVal = studentName ? studentName.charCodeAt(0) + studentName.charCodeAt(studentName.length - 1) : 92;
        const sequence = String((seedVal * 17) % 1000).padStart(6, '0');
        return `LH/${enrollYear}-${nextYear}/${sequence}`;
    };

    const regNo = getRegistrationNumber();

    // Grade calculation from exam score
    const getGradeInfo = () => {
        const score = Number(examScore || 90);
        if (score >= 90) return { grade: 'O', label: 'O (Outstanding)' };
        if (score >= 80) return { grade: 'A', label: 'A (Very Good)' };
        if (score >= 70) return { grade: 'B', label: 'B (Good)' };
        if (score >= 60) return { grade: 'C', label: 'C (Satisfactory)' };
        if (score >= 50) return { grade: 'D', label: 'D (Marginal)' };
        return { grade: 'F', label: 'F (Disqualified)' };
    };

    const gradeInfo = getGradeInfo();

    // Photo helper
    const getStudentPhotoUrl = () => {
        if (!studentPhoto) return null;
        return getMediaUrl(studentPhoto);
    };

    const photoUrl = getStudentPhotoUrl();

    // Syllabus bullet points based on CATIA/Course title
    const getSyllabusPoints = () => {
        if (courseDetails?.whatYouLearn && courseDetails.whatYouLearn.length > 0) {
            return courseDetails.whatYouLearn;
        }

        // Default standard syllabus for CATIA or fallback course
        const isCatia = courseTitle?.toLowerCase().includes('catia');
        if (isCatia || !courseDetails?.curriculum || courseDetails.curriculum.length === 0) {
            return [
                "User Interface, Editing Profile, Understanding Constraints.",
                "Introduction to Part Design, Creating Sketch- Based Features.",
                "Creating Reference Element, Applying Dress-up Features.",
                "Introduction to Assembly Design.",
                "Understanding Product Structure Tools.",
                "Understanding Constraints Toolbar, Introduction to Drafting.",
                "Creating New Drawing File, Creating Projection View.",
                "Creating Sectional Views, Creating Details Views.",
                "Creating Dimensions Using, Dimensions, Chained Dimensions.",
                "Cumulated Dimensions, Stacked Dimensions.",
                "Length Dimension, Angle Dimension.",
                "Radius Dimension, Diameter Dimension."
            ];
        }

        // Return lecture titles/curriculum items as bullet points
        const points = [];
        courseDetails.curriculum.forEach(section => {
            if (section.lectures) {
                section.lectures.forEach(lec => {
                    if (points.length < 12) points.push(lec.title);
                });
            }
        });
        return points;
    };

    const syllabus = getSyllabusPoints();

    // Course duration mapping
    const getDuration = () => {
        if (courseDetails?.duration) return courseDetails.duration;
        return "30 hours";
    };

    return (
        <div
            className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center z-50 p-4 md:p-6 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl transform scale-100 transition-all overflow-hidden flex flex-col h-[90vh] relative"
                onClick={e => e.stopPropagation()}
            >
                {/* Header Controls */}
                <div className="flex items-center justify-between px-8 py-5 border-b border-gray-100 bg-gray-50/50 no-print">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600">
                            <Award size={24} />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">Certificate & Transcript Preview</h2>
                            <p className="text-xs text-gray-500 font-medium">Verify both pages before saving or printing</p>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => window.print()}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-semibold text-sm transition-all flex items-center gap-2 shadow-lg shadow-emerald-600/10 cursor-pointer"
                        >
                            <Printer size={18} />
                            Print / Save PDF
                        </button>
                        <button
                            onClick={onClose}
                            className="p-2.5 hover:bg-gray-100 text-gray-400 hover:text-gray-600 rounded-xl transition-all cursor-pointer"
                            title="Close"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Modal View Scroll Area */}
                <div className="flex-1 overflow-y-auto p-8 space-y-12 bg-gray-100/50 flex flex-col items-center">
                    {loadingCourse ? (
                        <div className="flex flex-col items-center justify-center py-20">
                            <div className="animate-spin rounded-full h-10 w-10 border-4 border-emerald-600 border-t-transparent mb-3"></div>
                            <p className="text-sm font-semibold text-gray-600">Loading certificate data...</p>
                        </div>
                    ) : (
                        <>
                            {/* Page 1 (Portrait Certificate Container) */}
                            <div className="bg-white shadow-xl rounded-xl border border-gray-200 overflow-hidden relative page-preview-container page-portrait-sheet">
                                <div id="certificate-page-1" className="inner-sheet watermark-bg relative border-4 border-double border-emerald-800/20 m-4 flex flex-col justify-between">
                                    {/* Large Background Watermark Logo */}
                                    <div className="absolute inset-0 flex items-center justify-center opacity-[0.3] pointer-events-none select-none z-0">
                                        <img src="/companylogo.png" alt="Learning Hub Watermark" className="w-[360px] h-[360px] object-contain" />
                                    </div>

                                    {/* Green Corner Waves */}
                                    <svg className="absolute top-0 left-0 w-36 h-36 pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                                        <path d="M 0 0 L 100 0 C 70 20, 50 50, 0 100 Z" fill="url(#greenGrad)" />
                                        <path d="M 0 0 L 80 0 C 60 15, 45 40, 0 80 Z" fill="url(#lightGreenGrad)" opacity="0.6" />
                                        <path d="M 0 0 L 60 0 C 45 10, 35 30, 0 60 Z" fill="white" opacity="0.35" />

                                        <defs>
                                            <linearGradient id="greenGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#004d33" />
                                                <stop offset="100%" stopColor="#00b26f" />
                                            </linearGradient>
                                            <linearGradient id="lightGreenGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="#00b26f" />
                                                <stop offset="100%" stopColor="#40d39c" />
                                            </linearGradient>
                                        </defs>
                                    </svg>

                                    <svg className="absolute bottom-0 right-0 w-36 h-36 pointer-events-none rotate-180" viewBox="0 0 100 100" preserveAspectRatio="none">
                                        <path d="M 0 0 L 100 0 C 70 20, 50 50, 0 100 Z" fill="url(#greenGrad)" />
                                        <path d="M 0 0 L 80 0 C 60 15, 45 40, 0 80 Z" fill="url(#lightGreenGrad)" opacity="0.6" />
                                        <path d="M 0 0 L 60 0 C 45 10, 35 30, 0 60 Z" fill="white" opacity="0.35" />
                                    </svg>

                                    {/* Top Row: Logos & Photo */}
                                    <div className="flex justify-between items-center pt-6 px-8 relative z-10">
                                        {/* MSME Stamp */}
                                        <div className="flex flex-col items-center ml-10">
                                            <div style={{ width: '100px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <img src="/img/msme.png?v=3" alt="MSME Logo" className="max-w-full max-h-full object-contain mix-blend-multiply" />
                                            </div>
                                            <span className="text-[7px] font-black text-gray-600 tracking-wider mt-1 uppercase">UDYAM-WB-10-0144092</span>
                                        </div>

                                        {/* Circular Logo using /companylogo.png */}
                                        <div className="flex flex-col items-center">
                                            <img src="/companylogo.png" alt="Learning Hub Logo" className="w-40 h-40 object-contain" />
                                        </div>

                                        {/* ISO Stamp */}
                                        <div className="flex flex-col items-center mr-10">
                                            <div style={{ width: '100px', height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <img src="/img/iso.png?v=1" alt="ISO Stamp" className="max-w-full max-h-full object-contain mix-blend-multiply" />
                                            </div>
                                            <span className="text-[7px] font-black text-gray-700 tracking-wider mt-1 uppercase">305025011726Q</span>
                                        </div>
                                    </div>

                                    {/* Middle Core Text Content */}
                                    <div className="px-10 text-center relative z-10 mt-2 space-y-4">
                                        <h1 className="gothic-title text-[#004d33] mt-10 font-black text-2xl md:text-3xl">
                                            Certificate Of Completion
                                        </h1>

                                        <div className="space-y-6 pt-8 text-[11px] text-black tracking-wide max-w-2xl text-left">
                                            <div className="flex items-center gap-2">
                                                <span className="w-32 text-black font-black">IN</span>
                                                <span className="text-black font-bold">:</span>
                                                <span className="flex-1 font-normal text-black text-[13px] border-b border-black/30 pb-0.5 uppercase italic">
                                                    {courseTitle}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-32 text-black font-black">AWARDED TO</span>
                                                <span className="text-black font-bold">:</span>
                                                <span className="flex-1 font-normal text-black text-sm border-b border-black/30 pb-0.5 uppercase">
                                                    {studentName}
                                                </span>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <span className="w-32 text-black font-black">AT</span>
                                                <span className="text-black font-bold">:</span>
                                                <span className="flex-1 font-normal text-black border-b border-black/30 pb-0.5">
                                                    Learning Hub, Kolkata
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                <span className="w-32 text-black font-black">CERTIFICATE NO.</span>
                                                <span className="text-black font-bold">:</span>
                                                <span className="flex-1 font-normal text-black border-b border-black/30 pb-0.5 tracking-widest">
                                                    {regNo}
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bottom Signatures & Date of Issue Row */}
                                    <div className="flex justify-between items-end px-10 relative z-10 mt-14 mb-2">
                                        {/* MD Signature */}
                                        <div className="flex flex-col items-center">
                                            <span className="signature-font text-blue-700 text-2xl font-bold tracking-wider leading-none h-8 select-none">Sayantan Sinha Biswas</span>
                                            <div className="w-28 h-px bg-gray-800 mt-0.5"></div>
                                            <span className="text-[6.5px] font-black text-gray-900 uppercase tracking-widest mt-0.5">Sayantan Sinha Biswas</span>
                                            <span className="text-[6px] font-bold text-gray-800 uppercase tracking-wider">Managing Director</span>
                                        </div>

                                        {/* Center Head Signature */}
                                        <div className="flex flex-col items-center">
                                            <span className="signature-font text-blue-700 text-2xl font-bold tracking-wider leading-none h-8 select-none">Kakuli Sinha Biswas</span>
                                            <div className="w-28 h-px bg-gray-800 mt-0.5"></div>
                                            <span className="text-[6.5px] font-black text-gray-900 uppercase tracking-widest mt-0.5">Kakuli Sinha Biswas</span>
                                            <span className="text-[6px] font-bold text-gray-800 uppercase tracking-wider">Centre Head</span>
                                        </div>

                                        {/* Date of Issue */}
                                        <div className="flex flex-col items-center">
                                            <span className="text-[10px] font-black text-black tracking-wide h-5 select-none">{dates.issueDate}</span>
                                            <div className="w-28 h-px bg-gray-800 mt-0.5"></div>
                                            <span className="text-[6.5px] font-black text-black uppercase tracking-widest mt-0.5">&nbsp;</span>
                                            <span className="text-[6px] font-bold text-black uppercase tracking-wider">Date of Issue</span>
                                        </div>
                                    </div>

                                    {/* Seals and Verification Row */}
                                    <div className="flex justify-between items-center px-10 relative z-10 pt-14 pb-2">
                                        {/* QR Code Block */}
                                        <div className="flex flex-col items-center gap-1">
                                            <div className="border border-gray-300 p-1 bg-white shadow-sm rounded">
                                                <img
                                                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                                                        `Certificate No: ${regNo}\nStudent Name: ${studentName}\nCourse: ${courseTitle}\nLocation: Learning Hub, Kolkata`
                                                    )}`}
                                                    alt="Verification QR Code"
                                                    className="w-20 h-20 object-contain"
                                                />
                                            </div>
                                            <span className="text-[5.5px] font-black text-gray-700 tracking-wider uppercase text-center leading-tight">
                                                Scan QR Code<br />To Verify
                                            </span>
                                        </div>

                                        {/* Red Wax Seal */}
                                        <div className="flex-shrink-0 relative z-10 w-24 h-24 drop-shadow-md" title="Learning Hub Authorized Seal">
                                            {/* Outer Wax Shape */}
                                            <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full">
                                                <path d="M 50 5 C 59 4, 63 11, 71 9 C 79 7, 84 16, 90 18 C 96 20, 94 29, 97 37 C 100 45, 95 52, 95 61 C 95 70, 100 77, 97 85 C 94 93, 86 91, 79 95 C 72 99, 63 93, 55 93 C 47 93, 42 99, 34 97 C 26 95, 22 87, 15 83 C 8 79, 11 70, 9 61 C 7 52, 3 45, 5 37 C 7 29, 13 24, 18 16 C 23 8, 32 10, 39 6 C 46 2, 47 6, 50 5 Z" fill="#9e1b1b" />
                                                <circle cx="50" cy="50" r="33" fill="#8a1414" stroke="#700e0e" strokeWidth="0.75" />
                                            </svg>
                                            {/* Stamped Logo inside in Gold/Cream */}
                                            <div className="absolute inset-0 flex items-center justify-center p-3">
                                                <img src="/companylogo.png" alt="Learning Hub Stamped Logo" className="w-14 h-14 object-contain brightness-0 invert opacity-90 select-none pointer-events-none" />
                                            </div>
                                        </div>

                                        {/* Silver Hologram Sticker */}
                                        <div className="flex-shrink-0 relative z-10 w-24 h-24 rounded border border-gray-400/30 bg-gradient-to-br from-gray-100 via-gray-300 to-gray-400 shadow-sm flex items-center justify-center p-2" title="Learning Hub Genuine Hologram">
                                            <img src="/companylogo.png" alt="Learning Hub Hologram Logo" className="w-16 h-16 object-contain opacity-85 select-none pointer-events-none mix-blend-multiply" />
                                        </div>
                                    </div>

                                    {/* Bottom Description Text Block */}
                                    <div className="px-10 pb-4 pt-2 mt-auto relative z-10 flex flex-col items-center text-center space-y-1.5 select-none pointer-events-none">
                                        <p className="text-gray-700 font-extrabold text-[10px] leading-tight max-w-2xl">
                                            Learning Hub Elgin delivers industry-leading professional development through its expansive network of training facilities.<br />
                                            Our curriculum combines rigorous coursework with hands-on practice, ensuring students master<br />
                                            both foundational concepts and cutting-edge technologies. Each course is led by industry veterans who bring years of<br />
                                            practical experience to the classroom, providing students with real-world insights and technical expertise.
                                        </p>
                                        <div className="flex flex-col items-center text-[11px] font-black text-black leading-tight pt-1">
                                            <span>Corporate Office: 1 St Floor,4C, Benjamin Moloise Square,</span>
                                            <span>West Bengal, Kolkata - 700017. India</span>
                                            <span className="text-emerald-800 tracking-wider">www.learninghub.ind.in</span>
                                        </div>
                                        <p className="text-[#004d33] font-black tracking-widest text-[12.5px] uppercase pt-1">
                                            AN ISO 9001 - 2015 CERTIFIED COMPANY
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Page 2 (Portrait Transcript) Container */}
                            <div className="bg-white shadow-xl rounded-xl border border-gray-200 overflow-hidden relative page-preview-container page-portrait-sheet">
                                <div id="certificate-page-2" className="inner-sheet watermark-bg relative border-4 border-double border-emerald-800/20 m-4 flex flex-col justify-between">
                                    {/* Large Background Watermark Logo */}
                                    <div className="absolute inset-0 flex items-center justify-center opacity-[0.3] pointer-events-none select-none z-0">
                                        <img src="/companylogo.png" alt="Learning Hub Watermark" className="w-[360px] h-[360px] object-contain" />
                                    </div>

                                    {/* Green Corner Waves */}
                                    <svg className="absolute top-0 left-0 w-36 h-36 pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
                                        <path d="M 0 0 L 100 0 C 70 20, 50 50, 0 100 Z" fill="url(#greenGrad)" />
                                        <path d="M 0 0 L 80 0 C 60 15, 45 40, 0 80 Z" fill="url(#lightGreenGrad)" opacity="0.6" />
                                        <path d="M 0 0 L 60 0 C 45 10, 35 30, 0 60 Z" fill="white" opacity="0.35" />
                                    </svg>

                                    <svg className="absolute bottom-0 right-0 w-36 h-36 pointer-events-none rotate-180" viewBox="0 0 100 100" preserveAspectRatio="none">
                                        <path d="M 0 0 L 100 0 C 70 20, 50 50, 0 100 Z" fill="url(#greenGrad)" />
                                        <path d="M 0 0 L 80 0 C 60 15, 45 40, 0 80 Z" fill="url(#lightGreenGrad)" opacity="0.6" />
                                        <path d="M 0 0 L 60 0 C 45 10, 35 30, 0 60 Z" fill="white" opacity="0.35" />
                                    </svg>

                                    {/* Top Row: Details & Logo */}
                                    <div className="flex justify-between items-start pt-6 px-10 relative z-10">
                                        {/* Left Side: Student Info */}
                                        <div className="flex flex-col text-left space-y-2 mt-4  pt-24 text-[11px] font-black text-gray-900 tracking-wide">
                                            <div className="flex items-center gap-1">
                                                <span className="w-32 text-gray-900 uppercase tracking-widest text-[9.5px]">STUDENT NAME</span>
                                                <span className="text-gray-900">:</span>
                                                <span className="font-extrabold text-gray-900 border-b border-gray-400 pb-0.5 uppercase pl-2 pr-4">{studentName}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className="w-32 text-gray-900 uppercase tracking-widest text-[9.5px]">CERTIFICATE NO.</span>
                                                <span className="text-gray-900">:</span>
                                                <span className="font-black text-gray-900 border-b border-gray-400 pb-0.5 tracking-wider pl-2 pr-4">{regNo}</span>
                                            </div>
                                            <div className="flex items-center gap-1">
                                                <span className="w-32 text-gray-900 uppercase tracking-widest text-[9.5px]">GRADE</span>
                                                <span className="text-gray-900">:</span>
                                                <span className="font-black text-emerald-900 border-b border-gray-400 pb-0.5 text-sm pl-2 pr-4">{gradeInfo.grade}</span>
                                            </div>
                                        </div>

                                        {/* Right Side: Logo using /companylogo.png */}
                                        <div className="flex flex-col items-end">
                                            <img src="/companylogo.png" alt="Learning Hub Logo" className="w-20 h-20 object-contain" />
                                        </div>
                                    </div>

                                    {/* Syllabus / What You Will Learn Section */}
                                    <div className="px-10 mt-6 text-left relative z-10 flex-1 flex flex-col justify-start">
                                        <h2 className="text-[#004d33] font-black tracking-wider text-sm uppercase mb-4 border-b border-emerald-900/20 pb-1">
                                            WHAT YOU LEARN
                                        </h2>
                                        <ul className="grid grid-cols-1 gap-3 pl-4 list-none text-[11.5px] font-black text-black tracking-wide">
                                            {syllabus.map((point, index) => (
                                                <li key={index} className="flex items-start gap-2.5 leading-relaxed">
                                                    <span className="text-[#004d33] font-bold text-[16px] leading-none mt-0.5">•</span>
                                                    <span className="text-black font-bold">{point}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    {/* Grade Scale Table & Bottom Brand */}
                                    <div className="px-10 pb-8 pt-4 relative z-10 mt-auto flex flex-col items-center">
                                        {/* Faint Center Text Watermark */}
                                        <div className="text-[14px] font-black text-emerald-900/5 uppercase tracking-widest select-none mb-3">FOR EXCELLENCE</div>

                                        {/* Table */}
                                        <div className="w-80 border border-gray-400 bg-white/80 backdrop-blur-sm self-start">
                                            <div className="grid grid-cols-2 border-b border-gray-400 text-center font-bold text-[11px] bg-gray-100 py-2 uppercase text-gray-700">
                                                <div className="border-r border-gray-400">Grade Scale</div>
                                                <div>Grade</div>
                                            </div>
                                            <div className="grid grid-cols-2 border-b border-gray-400 text-center text-[10px] font-bold py-1.5 text-gray-800">
                                                <div className="border-r border-gray-400">100%-90%</div>
                                                <div>O (Outstanding)</div>
                                            </div>
                                            <div className="grid grid-cols-2 border-b border-gray-400 text-center text-[10px] font-semibold py-1.5 text-gray-800">
                                                <div className="border-r border-gray-400">89%-80%</div>
                                                <div>A (Very Good)</div>
                                            </div>
                                            <div className="grid grid-cols-2 border-b border-gray-400 text-center text-[10px] font-semibold py-1.5 text-gray-800">
                                                <div className="border-r border-gray-400">79%-70%</div>
                                                <div>B (Good)</div>
                                            </div>
                                            <div className="grid grid-cols-2 border-b border-gray-400 text-center text-[10px] font-semibold py-1.5 text-gray-800">
                                                <div className="border-r border-gray-400">69%-60%</div>
                                                <div>C (Satisfactory)</div>
                                            </div>
                                            <div className="grid grid-cols-2 border-b border-gray-400 text-center text-[10px] font-semibold py-1.5 text-gray-800">
                                                <div className="border-r border-gray-400">59%-50%</div>
                                                <div>D (Marginal)</div>
                                            </div>
                                            <div className="grid grid-cols-2 text-center text-[10px] font-semibold py-1.5 text-gray-800">
                                                <div className="border-r border-gray-400">Below 49%</div>
                                                <div>F (Disqualified)</div>
                                            </div>
                                        </div>

                                        <p className="text-[10px] font-black text-black uppercase tracking-widest mt-8">
                                            AN ISO 9001 - 2015 CERTIFIED COMPANY
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* Custom Embedded CSS */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=UnifrakturCook:wght@700&family=UnifrakturMaguntia&family=Cinzel:wght@500;700;800;900&family=Caveat:wght@700&family=Inter:wght@400;500;600;700;800;900&display=swap');
                
                .cinzel-title {
                    font-family: 'Cinzel', Georgia, serif;
                }
                .gothic-title {
                    font-family: 'UnifrakturCook', 'UnifrakturMaguntia', Georgia, serif;
                }
                .signature-font {
                    font-family: 'Caveat', cursive, sans-serif;
                }
                
                /* Watermark background using repeating inline SVG pattern */
                .watermark-bg {
                    background-color: #fafdfb;
                    background-image: url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20width%3D%27110%27%20height%3D%2726%27%20viewBox%3D%270%200%20110%2026%27%3E%3Ctext%20transform%3D%27rotate%28-20%2055%2013%29%27%20fill%3D%27rgba%280%2C%2077%2C%2051%2C%200.08%29%27%20font-size%3D%277%27%20font-family%3D%27Inter%2C%20sans-serif%27%20font-weight%3D%27900%27%20letter-spacing%3D%271%27%20x%3D%2715%27%20y%3D%2716%27%3ELEARNING%20HUB%20%C2%AE%3C%2Ftext%3E%3C%2Fsvg%3E");
                    background-repeat: repeat;
                }

                /* Hologram Silver gradient sticker */
                .holo-sticker {
                    width: 44px;
                    height: 44px;
                    background: linear-gradient(135deg, #e6e6e6 0%, #ffffff 25%, #c8c8c8 50%, #e1e1e1 75%, #b4b4b4 100%);
                    border-radius: 4px;
                    border: 1px solid rgba(0,0,0,0.08);
                    box-shadow: inset 0 0 8px rgba(255,255,255,0.9), 0 3px 6px rgba(0,0,0,0.1);
                    position: relative;
                    overflow: hidden;
                    color: rgba(0,0,0,0.12);
                }
                .holo-sticker::before {
                    content: '';
                    position: absolute;
                    inset: 3px;
                    border: 0.75px dashed rgba(255,255,255,0.7);
                    border-radius: 50%;
                }

                /* On-Screen Preview Containers */
                .page-portrait-sheet {
                    width: 210mm;
                    height: 297mm;
                    position: relative;
                    background: white;
                    flex-shrink: 0;
                    box-sizing: border-box;
                }
                
                .inner-sheet {
                    width: calc(210mm - 32px);
                    height: calc(297mm - 32px);
                    box-sizing: border-box;
                }

                /* Responsiveness for On-Screen Modal on smaller viewports */
                @media (max-width: 1024px) {
                    .page-portrait-sheet {
                        transform: scale(0.75);
                        margin-bottom: -74mm;
                    }
                }
                @media (max-width: 768px) {
                    .page-portrait-sheet {
                        transform: scale(0.5);
                        margin-bottom: -148.5mm;
                    }
                }
                @media (max-width: 500px) {
                    .page-portrait-sheet {
                        transform: scale(0.35);
                        margin-bottom: -193mm;
                    }
                }

                @page {
                    size: A4 portrait;
                    margin: 0;
                }
                
                @media print {
                     html, body {
                         height: auto !important;
                         overflow: visible !important;
                         background: white !important;
                     }
                     /* Set main react container height to 0 during print to avoid extra blank pages */
                     #root {
                         height: 0 !important;
                         overflow: visible !important;
                         margin: 0 !important;
                         padding: 0 !important;
                     }
                     * {
                         -webkit-print-color-adjust: exact !important;
                         print-color-adjust: exact !important;
                     }
                     /* Reset everything on screen */
                     body * {
                         visibility: hidden;
                     }
                     .fixed.inset-0, .fixed.inset-0 * {
                         visibility: visible;
                     }
                     .fixed.inset-0 {
                         position: absolute !important;
                         left: 0 !important;
                         top: 0 !important;
                         width: 210mm !important;
                         height: auto !important;
                         background: white !important;
                         padding: 0 !important;
                         margin: 0 !important;
                         overflow: visible !important;
                         z-index: 99999;
                     }
                     .fixed.inset-0 > div {
                         position: absolute !important;
                         left: 0 !important;
                         top: 0 !important;
                         width: 210mm !important;
                         height: auto !important;
                         max-width: none !important;
                         background: white !important;
                         box-shadow: none !important;
                         border-radius: 0 !important;
                         transform: none !important;
                         overflow: visible !important;
                         margin: 0 !important;
                         padding: 0 !important;
                     }
                     .fixed.inset-0 > div > div:not(.no-print) {
                         position: static !important;
                         display: block !important;
                         width: 210mm !important;
                         height: auto !important;
                         padding: 0 !important;
                         margin: 0 !important;
                         background: white !important;
                         overflow: visible !important;
                     }
                     .no-print {
                         display: none !important;
                     }
                     .page-preview-container {
                         box-shadow: none !important;
                         border: none !important;
                         border-radius: 0 !important;
                         margin: 0 !important;
                         padding: 0 !important;
                         transform: none !important;
                         overflow: visible !important;
                     }
                     
                     .page-portrait-sheet {
                         width: 210mm !important;
                         height: 100vh !important;
                         page-break-after: always;
                         break-after: page;
                         display: block !important;
                         position: relative !important;
                     }
                     
                     .page-portrait-sheet:last-child {
                         page-break-after: avoid;
                         break-after: avoid;
                     }
                     
                     .inner-sheet {
                         width: calc(210mm - 32px) !important;
                         height: calc(100vh - 32px) !important;
                         display: flex !important;
                     }
                }
            `}</style>
        </div>
    );
};

export default CertificateModal;
